// Frequency / Tuning control — compact pill button + popover.
//
// Lives next to the volume slider in the persistent player. Opens a popover
// containing:
//  1. Believer Listening Choices (preset chips)
//  2. Custom controls (A4 slider + tone layer freq + tone volume) — only
//     visible when "Custom" is the active preset.
//  3. A truthful, non-medical disclaimer.
//
// Web Audio plumbing lives in PlayerContext + lib/webAudio.js. This component
// is purely a controller — it dispatches preset/preference updates.

import React, { useEffect, useRef, useState } from "react";
import { X, AudioWaveform, Check, Info } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { PRESETS, centsFromA4, CROSSFADE_OPTIONS, isSignedInForPrefs } from "../lib/frequencyPrefs";

// Solfeggio meanings displayed in the "Why these frequencies?" explainer.
// Sourced from common solfeggio traditions — these are spiritual/symbolic
// associations, NOT scientific or medical claims. The disclaimer block makes
// that explicit.
const FREQ_EXPLAINER = [
  { hz: 174, name: "Foundation",      meaning: "grounding · settling · steadiness" },
  { hz: 285, name: "Renewal",         meaning: "restoration · refreshment" },
  { hz: 396, name: "Release",         meaning: "letting go of fear & guilt" },
  { hz: 417, name: "Transformation",  meaning: "facilitating change & momentum" },
  { hz: 432, name: "Sabbath",         meaning: "warmer cosmic tuning · rest" },
  { hz: 440, name: "Standard",        meaning: "modern concert pitch · default" },
  { hz: 528, name: "Reflection",      meaning: "love · prayerful stillness" },
  { hz: 639, name: "Unity",           meaning: "brotherly love · connection" },
  { hz: 741, name: "Awakening",       meaning: "intuition · clarity" },
  { hz: 852, name: "Focus",           meaning: "airy concentration · study" },
  { hz: 963, name: "Sacred Light",    meaning: "consciousness · openness" },
];

const PRESET_ORDER = [
  "original", "sabbath_rest",
  "renewal", "release", "transformation",
  "prayerful_reflection", "unity", "awakening",
  "study_focus", "divine_light", "deep_sleep",
  "custom",
];

const TONE_OPTIONS = [
  { hz: 0,   label: "None" },
  { hz: 174, label: "174 Hz · grounding" },
  { hz: 285, label: "285 Hz · restoration" },
  { hz: 396, label: "396 Hz · release" },
  { hz: 417, label: "417 Hz · transformation" },
  { hz: 528, label: "528 Hz · reflective" },
  { hz: 639, label: "639 Hz · unity" },
  { hz: 741, label: "741 Hz · awakening" },
  { hz: 852, label: "852 Hz · airy" },
  { hz: 963, label: "963 Hz · divine light" },
];

export default function FrequencyControl() {
  const { listeningPrefs, updateListeningPrefs, setListeningPreset } = usePlayer();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  // "Saved to your profile" confirmation pill — shown briefly when a
  // signed-in user changes any listening pref. Skips the initial mount so
  // it doesn't flash on every page load.
  const [savedPill, setSavedPill] = useState(false);
  const prefsSigRef = useRef(null);
  const savedTimerRef = useRef(null);
  useEffect(() => {
    if (!listeningPrefs) return;
    const sig = JSON.stringify({
      p: listeningPrefs.preset,
      a: listeningPrefs.a4_hz,
      t: listeningPrefs.tone_hz,
      v: listeningPrefs.tone_volume,
      c: listeningPrefs.crossfade_seconds,
    });
    // First time we see a sig — just record it, don't show the pill.
    if (prefsSigRef.current === null) {
      prefsSigRef.current = sig;
      return;
    }
    if (sig === prefsSigRef.current) return;
    prefsSigRef.current = sig;
    if (!isSignedInForPrefs()) return; // anonymous → no server save → no pill
    setSavedPill(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedPill(false), 2200);
  }, [listeningPrefs]);
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const preset = listeningPrefs?.preset || "original";
  const isCustom = preset === "custom";
  const labelShort =
    preset === "sabbath_rest" ? "432 Hz"
    : preset === "study_focus" ? "Focus"
    : preset === "prayerful_reflection" ? "Reflect"
    : preset === "deep_sleep" ? "Deep"
    : preset === "custom" ? "Custom"
    : "Original";

  // Close on outside click + Esc
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const cents = centsFromA4(listeningPrefs.a4_hz || 440);
  const centsLabel = `${cents >= 0 ? "+" : ""}${cents.toFixed(2)} cents`;

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="frequency-toggle"
        title="Frequency / Tuning"
        aria-expanded={open}
        aria-label="Frequency / Tuning"
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7] hover:border-[#C5A059]/40 text-xs transition-colors"
      >
        <AudioWaveform className="w-3.5 h-3.5" strokeWidth={1.5} />
        <span className="hidden md:inline">Freq:</span>
        <span className="text-[#C5A059]">{labelShort}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Frequency tuning options"
          data-testid="frequency-panel"
          className="absolute z-50 bottom-full right-0 mb-2 w-[340px] rounded-xl border border-[#C5A059]/30 bg-[#0E1014]/95 backdrop-blur-md shadow-[0_0_50px_rgba(0,0,0,0.7)] p-4 text-left"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="overline text-[#C5A059] text-[10px]">Believer Listening Choices</div>
              <div className="text-[#FDFBF7] font-serif text-base leading-tight">Frequency-tuned listening</div>
              {savedPill && (
                <div
                  data-testid="frequency-saved-pill"
                  aria-live="polite"
                  className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-400 transition-opacity"
                >
                  <Check className="w-3 h-3" strokeWidth={2} />
                  <span>Saved to your profile</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="frequency-close"
              aria-label="Close"
              className="text-[#A1A4AB] hover:text-[#FDFBF7]"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          {/* Preset grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {PRESET_ORDER.map((key) => {
              const p = PRESETS[key];
              const active = preset === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setListeningPreset(key)}
                  data-testid={`frequency-preset-${key}`}
                  className={`relative text-left rounded-lg border px-3 py-2 transition-all ${
                    active
                      ? "border-[#C5A059] bg-[#C5A059]/10 text-[#FDFBF7]"
                      : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-sm">{p.label}</span>
                    {active && <Check className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={2} />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="text-[11px] text-[#A1A4AB] mb-3 leading-snug" data-testid="frequency-blurb">
            {PRESETS[preset].blurb}
          </div>

          {/* Crossfade — independent of preset, applies to track→track transitions */}
          <div className="pt-3 border-t border-[#22262E]">
            <div className="flex items-center justify-between mb-1.5">
              <label className="overline text-[10px] text-[#A1A4AB]" htmlFor="crossfade-row">
                Crossfade
              </label>
              <span className="text-[10px] text-[#6C707A]">
                {Number(listeningPrefs.crossfade_seconds || 0) === 0 ? "Off" : `${Math.round(listeningPrefs.crossfade_seconds)}s`}
              </span>
            </div>
            <div id="crossfade-row" className="flex gap-1.5" data-testid="crossfade-options">
              {CROSSFADE_OPTIONS.map((s) => {
                const active = Math.round(Number(listeningPrefs.crossfade_seconds || 0)) === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => updateListeningPrefs({ crossfade_seconds: s })}
                    data-testid={`crossfade-option-${s}`}
                    className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition-colors ${
                      active
                        ? "bg-[#C5A059] text-[#060709] border-[#C5A059]"
                        : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
                    }`}
                  >
                    {s === 0 ? "Off" : `${s}s`}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-[#6C707A] mt-1.5 leading-snug">
              Equal-power audio crossfade between tracks. Skip uses a shorter fade. Stop is always instant.
            </div>
          </div>

          {/* Custom controls — only when preset = custom */}
          {isCustom && (
            <div className="space-y-3 pt-3 border-t border-[#22262E]">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="freq-a4" className="overline text-[10px] text-[#A1A4AB]">
                    A4 tuning
                  </label>
                  <span className="text-xs text-[#FDFBF7]" data-testid="frequency-a4-readout">
                    {Math.round(listeningPrefs.a4_hz)} Hz · {centsLabel}
                  </span>
                </div>
                <input
                  id="freq-a4"
                  type="range"
                  min="400"
                  max="480"
                  step="1"
                  value={listeningPrefs.a4_hz}
                  onChange={(e) => updateListeningPrefs({ a4_hz: Number(e.target.value) })}
                  data-testid="frequency-a4-slider"
                  className="w-full accent-[#C5A059]"
                />
              </div>

              <div>
                <label htmlFor="freq-tone" className="overline text-[10px] text-[#A1A4AB] block mb-1">
                  Tone layer
                </label>
                <select
                  id="freq-tone"
                  value={listeningPrefs.tone_hz}
                  onChange={(e) => updateListeningPrefs({ tone_hz: Number(e.target.value) })}
                  data-testid="frequency-tone-select"
                  className="w-full bg-[#0a0a14] border border-[#22262E] rounded-md px-2 py-1.5 text-sm text-[#FDFBF7] focus:border-[#C5A059] outline-none"
                >
                  {TONE_OPTIONS.map((t) => (
                    <option key={t.hz} value={t.hz}>{t.label}</option>
                  ))}
                </select>
              </div>

              {listeningPrefs.tone_hz > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="freq-tone-vol" className="overline text-[10px] text-[#A1A4AB]">
                      Tone layer volume
                    </label>
                    <span className="text-xs text-[#FDFBF7]" data-testid="frequency-tone-volume-readout">
                      {Math.round(listeningPrefs.tone_volume * 100)}%
                    </span>
                  </div>
                  <input
                    id="freq-tone-vol"
                    type="range"
                    min="0"
                    max="0.2"
                    step="0.005"
                    value={listeningPrefs.tone_volume}
                    onChange={(e) => updateListeningPrefs({ tone_volume: Number(e.target.value) })}
                    data-testid="frequency-tone-volume-slider"
                    className="w-full accent-[#C5A059]"
                  />
                </div>
              )}
            </div>
          )}

          <details className="mt-3 pt-3 border-t border-[#22262E] group" data-testid="frequency-why-details">
            <summary
              className="flex items-center gap-1.5 text-[11px] text-[#C5A059] hover:text-[#FDFBF7] cursor-pointer list-none select-none"
              data-testid="frequency-why-toggle"
            >
              <Info className="w-3 h-3" strokeWidth={1.5} />
              <span className="overline tracking-widest">Why these frequencies?</span>
              <span className="ml-auto text-[10px] text-[#6C707A] group-open:hidden">show</span>
              <span className="ml-auto text-[10px] text-[#6C707A] hidden group-open:inline">hide</span>
            </summary>
            <div className="mt-2 space-y-1 text-[10.5px] text-[#A1A4AB] leading-snug" data-testid="frequency-why-list">
              {FREQ_EXPLAINER.map((f) => (
                <div key={f.hz} className="flex items-baseline gap-2">
                  <span className="text-[#C5A059] font-medium tabular-nums w-12 flex-shrink-0">{f.hz} Hz</span>
                  <span className="text-[#FDFBF7] flex-shrink-0">{f.name}</span>
                  <span className="text-[#6C707A]">· {f.meaning}</span>
                </div>
              ))}
            </div>
          </details>

          <div className="mt-3 pt-3 border-t border-[#22262E] text-[10px] text-[#6C707A] leading-snug" data-testid="frequency-disclaimer">
            <strong className="text-[#A1A4AB] not-italic">Disclaimer:</strong> Yah-Wave's frequency presets are <em>spiritual & listening-experience curations</em>, drawn from solfeggio and tuning traditions. They are <strong className="text-[#A1A4AB]">not medical, therapeutic, diagnostic, or scientifically validated claims</strong>. These settings only adjust playback — they do not treat, cure, or prevent any condition. Always consult a qualified professional for medical concerns.
          </div>
        </div>
      )}
    </div>
  );
}
